/* eslint-disable jsx-a11y/alt-text */
import { useMutation } from "@apollo/client";
import useText from "constants/text";
import { useAlertDispatch } from "contexts/AlertContext";
import ConfirmDialog from "domains/CashManagement/components/dialogs/ConfirmDialog";
import DisplayFileDialog from "domains/CashManagement/components/dialogs/DisplayFileDialog";
import ValidateDialog from "domains/CashManagement/components/dialogs/ValidateDialog";
import { History } from "domains/CashManagement/components/History";
import { remittanceActiveCardIdentifier } from "domains/CashManagement/data";
import { useRemittanceDetailStyles } from "domains/CashManagement/style";
import { RemittanceDetailProps } from "domains/CashManagement/types";
import { ParcelRecapStatus, RemittanceBadge, RemittanceType } from "generated/graphql";
import { CONFIRM_REMITTANCE } from "graphql/cashManagement/mutations";
import moment from "moment";
import React, { useState } from "react";
import { Button } from "ui/Button";
import { formatAmount } from "utils";
import { isSuccess } from "utils/https";

export const RemittanceDetail: React.FC<RemittanceDetailProps> = ({
  remittance,
  setActiveCard,
  setRemittanceStatus,
  setRemittanceDetails,
}) => {
  const classes = useRemittanceDetailStyles();
  const [openModal, setOpenModal] = useState(false);
  const [openValidationModal, setOpenValidationModal] = useState(false);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [isConfirm, setIsConfirm] = useState(false);
  const [reason, setReason] = React.useState<string>("");
  const dispatchAlert = useAlertDispatch();
  const [confirmRemittance, { loading }] = useMutation(CONFIRM_REMITTANCE);
  const { text } = useText();

  const onCloseModal = () => {
    setOpenModal(false);
    setIsConfirm(false);
  };

  const OnCloseValidationModal = () => {
    setOpenValidationModal(false);
    setIsConfirm(false);
  };

  const onCloseConfirmModal = () => {
    setOpenConfirmModal(false);
    setIsConfirm(false);
    setReason(null);
  };

  const onOpenModal = () => setOpenModal(true);
  const onOpenConfiemModal = () => setOpenConfirmModal(true);
  const onOpenValidationModal = () => {
    setOpenConfirmModal(false);
    setOpenValidationModal(true);
    setIsConfirm(false);
  };

  const onOpenDirectvalidation = () => {
    setOpenValidationModal(true);
    setIsConfirm(true);
  };

  const getNextStepInfo = () => {
    setRemittanceDetails(null);
    setOpenValidationModal(false);
    setActiveCard(
      remittanceActiveCardIdentifier[isConfirm ? ParcelRecapStatus.RemittanceConfirmed : ParcelRecapStatus.NotReceived]
    );
    setRemittanceStatus(isConfirm ? ParcelRecapStatus.RemittanceConfirmed : ParcelRecapStatus.NotReceived);
  };

  const onValidate = () => {
    confirmRemittance({
      variables: {
        input: {
          remittanceID: remittance?._id,
          remittance: {
            declineReceptionReason: reason,
            badge: isConfirm ? RemittanceBadge.Received : RemittanceBadge.NotReceived,
            receiverName: remittance?.receiverName,
            receiverPhone: remittance?.receiverPhone,
            isReceptionConfirmed: isConfirm,
          },
        },
      },
    }).then(({ data }) => {
      if (isSuccess(data)) {
        dispatchAlert({
          message: isConfirm
            ? text.cash_management.confirm_remittance_succes_message
            : text.cash_management.not_receive_remittance_succes_message,
          severity: "success",
        });
        getNextStepInfo();
      } else {
        dispatchAlert({
          message: data?.results?.message,
          severity: "error",
        });
      }
    });
  };

  const isToConfirm = React.useMemo(
    () =>
      remittance?.badge !== RemittanceBadge.NotReceived &&
      remittance?.status !== ParcelRecapStatus.RemittanceInHouse &&
      remittance?.status !== ParcelRecapStatus.RemittanceConfirmed,
    [remittance]
  );
  return (
    <div className={classes.container}>
      <div>
        {remittance?.badge === RemittanceBadge.NotReceived && (
          <>
            <div className={classes.error}>
              <p className="icon">i</p>
              <p>{text.all.report_problem}</p>
            </div>
            <p className={classes.notReceivedMessage}>{remittance?.declineReceptionReason}</p>
          </>
        )}
        <div className={classes.head}>
          <p className={classes.remittanceTitle}>
            {text.cash_management.remittance} {remittance?.remittanceUID}
          </p>
          <p className={classes.amount}>
            {" "}
            {remittance?.type === RemittanceType.MobileMoney
              ? formatAmount(remittance?.finalAmount)
              : formatAmount(remittance?.amountToRemit)}
          </p>
        </div>
        <small className={classes.createdAt}>
          {text.all.created_on} {moment(remittance?.createdAt).format("YYYY/MM/DD") || "N/A"}
        </small>
        <>
          <div className={classes.lineHeader}>
            <p className={classes.lineLabel}>{text.all.receiver}</p>
            <p className={classes.line} />
          </div>
          <div className={classes.render}>
            <span className={classes.label}>{text.all.name} : </span>
            <span className={classes.value}>{remittance?.receiverName}</span>
          </div>
          <div className={classes.render}>
            <span className={classes.label}>{text.all.phone_number} : </span>
            <span className={classes.value}>{remittance?.receiverPhone}</span>
          </div>
        </>
        <>
          <div className={classes.lineHeader}>
            <p className={classes.lineLabel}>{text.all.costs}</p>
            <p className={classes.line} />
          </div>
          {remittance?.costs && <span className={classes.value}>{remittance?.costs}</span>}
          <div className={classes.render}>
            <span className={classes.label}>{text.all.amount} : </span>
            <span className={classes.value}>{formatAmount(remittance?.amountToRemit)}</span>
          </div>
        </>

        <>
          <div className={classes.lineHeader}>
            <p className={classes.lineLabel}>{text.all.proof}</p>
            <p className={classes.line} />
          </div>
          <Button
            disabled={remittance?.status === ParcelRecapStatus.RemittanceInHouse}
            className={classes.proofButton}
            onClick={onOpenModal}
          >
            <p className={classes.buttonLabel}>{text.cash_management.view_proof}</p>
          </Button>
        </>
        <>
          {!!remittance?.additionalInfo && (
            <>
              <div className={classes.lineHeader}>
                <p className={classes.lineLabel}>{text.all.additional_infos}</p>
                <p className={classes.line} />
              </div>
              <span className={classes.value}>{remittance?.additionalInfo}</span>
            </>
          )}
        </>
        <div>
          {remittance?.activities?.length > 0 && (
            <>
              <div className={classes.lineHeader}>
                <p className={classes.lineLabel}>{text.all.history}</p>
                <p className={classes.line} />
              </div>
              <div className={classes.history}>
                <History activities={remittance?.activities} />
              </div>
            </>
          )}
        </div>
      </div>

      {isToConfirm && (
        <div className={classes.footer}>
          <Button className={classes.rejectButton} onClick={onOpenConfiemModal}>
            {text.all.report_problem.toUpperCase()}
          </Button>
          <Button className={classes.confirmButton} onClick={onOpenDirectvalidation}>
            {" "}
            {text.all.confirm.toUpperCase()}{" "}
          </Button>
        </div>
      )}

      <DisplayFileDialog open={openModal} files={remittance?.proof} onClose={onCloseModal} />

      <ConfirmDialog
        open={openConfirmModal}
        onClose={onCloseConfirmModal}
        onConfirm={onOpenValidationModal}
        reason={reason}
        setReason={setReason}
      />

      <ValidateDialog
        onClose={OnCloseValidationModal}
        isConfirm={isConfirm}
        onConfirm={onValidate}
        open={openValidationModal}
      />
    </div>
  );
};
